// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { get } from 'lodash';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { Confirmation } from 'speed-js-react';
import { withConfirmation, connectAPI } from 'speed-js-react';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import TimelineIcon from 'esm-web-pro/components/layout/sidePanel/icons/TimelineIcon';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import PlusIcon from 'esm-web-pro/components/layout/sidePanel/icons/Plus';
import TransformGisPoint from 'esm-web-pro/containers/pages/network/objects/gispoint/TransformPoint';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import GisPointEditor from './GisPointEditor';

type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, skipSensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>, point: Object,
    onEdit: Function,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    history: Object,
    match: Object,
    map: Object,
    deleteObject: Object,
    resource: string,
    editor: BaseEditor,
    objectLayer: Object,
    updateObject: Function,
}

const msg = defineMessages({
    deleteTitle: {
        id: 'delete-point-title',
        defaultMessage: 'Are you sure you want to delete the point? All related points from layer 1.2 will be '
            + 'deleted also.',
    },
    cancelDelete: {
        id: 'delete-point-cancel',
        defaultMessage: 'Cancel',
    },
    confirmDelete: {
        id: 'delete-point-confirm',
        defaultMessage: 'Confirm',
    },
    cantDelete: {
        id: 'cant-delete-point',
        defaultMessage: 'The point is termination point for the line/s. You should delete the line/s first.',
    },
    title: {
        id: 'edit-gispoint-title',
        defaultMessage: 'Edit Gispoint',
    },
});

const addContentTitle = (
    <FormattedMessage
        id="add-content-title"
        defaultMessage="Infrastructure Object Configurator"
    />
);

const GIS_POINT_EDIT_KEY = 'GIS_POINT_EDIT_KEY';
const ADD_CONTENT_KEY = 'ADD_CONTENT_KEY';

class GisPointActions extends React.Component <Props> {
    handleClose = (skipSensitivity = false) => {
        const { closePanel } = this.props;
        closePanel(GIS_POINT_EDIT_KEY, skipSensitivity);
    };

    handleContentClose = (skipSensitivity = false) => {
        const { closePanel } = this.props;
        closePanel(ADD_CONTENT_KEY, skipSensitivity);
    };

    handleSave = (newValues) => {
        const { onEdit } = this.props;
        onEdit(newValues);
        this.handleClose(true);
    };

    getActions = () => {
        const {
            map: { is_draft: isDraft }, intl: { formatMessage }, history, match, point, resource, editor, openPanel,
        } = this.props;
        const actions = [];

        if (isDraft) {
            actions.push(
                {
                    key: `${point.id}-add-content`,
                    icon: <PlusIcon />,
                    callback: () => openPanel(ADD_CONTENT_KEY, addContentTitle, match.url, true),
                    disabled: editor.state.loading,
                    tooltip: formatMessage(actionMessages.addContent),
                    marginVertical: '15px',
                    radius: '0%',
                    color: 'transparent',
                },
                {
                    key: `${point.id}-edit`,
                    icon: <EditIcon size="small" />,
                    callback: () => openPanel(
                        GIS_POINT_EDIT_KEY,
                        '',
                        match.url,
                        true,
                    ),
                    disabled: editor.state.loading,
                    tooltip: formatMessage(actionMessages.edit),
                    marginVertical: '15px',
                    radius: '0%',
                    color: 'transparent',
                },
            );
        }

        actions.push({
            icon: <TimelineIcon />,
            callback: () => {
                history.push(`${match.url}/timeline/${resource}/${point.identity}`.replace('//', '/'));
            },
            tooltip: formatMessage(actionMessages.timeline),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });

        if (isDraft) {
            actions.push({
                key: `${point.id}-delete`,
                icon: <DeleteIcon size="small" />,
                callback: () => this.deletePoint(),
                disabled: editor.state.loading,
                tooltip: formatMessage(actionMessages.delete),
                marginVertical: '15px',
                radius: '0%',
                color: 'transparent',
            });
        }

        return actions;
    };


    deleteLayerFromMap = (response) => {
        const {
            confirm, history, objectLayer, intl: { formatMessage },
        } = this.props;

        if (response.status === HTTP_400_BAD_REQUEST) {
            confirm({
                message: response.data[0],
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        } else {
            objectLayer.remove();
            history.goBack();
        }
    };

    deletePoint = () => {
        const {
            intl: { formatMessage }, confirm, point, deleteObject,
        } = this.props;
        if (point.lines_count < 1) {
            confirm({
                message: formatMessage(msg.deleteTitle),
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                    confirm: {
                        label: formatMessage(msg.confirmDelete),
                        callback: () => deleteObject('gisPoint', point.uuid).then((response) => {
                            this.deleteLayerFromMap(response);
                        }),
                        type: 'positive',
                    },
                },
            });
        } else {
            confirm({
                message: formatMessage(msg.cantDelete),
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        }
    };

    render() {
        const {
            point, map, resource, objectLayer, history, match, updateObject, editor, intl: { formatMessage },
        } = this.props;

        return (
            <>
                <SidePane
                    id={GIS_POINT_EDIT_KEY}
                    key={`${point.uuid}-edit-panel`}
                    // TODO: Add title to GisPointEditor
                    title={`${formatMessage(msg.title)} ${point.sequence}`}
                >
                    <GisPointEditor
                        id={GIS_POINT_EDIT_KEY}
                        point={point}
                        objectLayer={objectLayer}
                        onClose={this.handleClose}
                        onSave={this.handleSave}
                        mapUuid={map.uuid}
                        resource={resource}
                        match={match}
                        history={history}
                        updateObject={updateObject}
                        editor={editor}
                    />
                </SidePane>
                <SidePane
                    id={ADD_CONTENT_KEY}
                    key={`${point.uuid}-add-content-panel`}
                    title={addContentTitle}
                    sensitive
                >
                    <TransformGisPoint
                        onClose={this.handleContentClose}
                        onSave={() => this.handleContentClose(true)}
                        point={point}
                        pointId={point.id}
                        map={map}
                        loadFeatures={editor._loadFeatures}
                        focus={editor.focus}
                    />
                </SidePane>
                <MainActions main actions={this.getActions()} />
            </>
        );
    }
}

const apiCallback = (apiClient: ApiClient) => ({
    deleteObject: {
        send: (resource: string, id: string) => get(apiClient.resources,
            resource).delete(id),
    },
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

export default connectAPI(apiCallback)(
    withRouter(
        withConfirmation(
            injectIntl(
                connect(null, mapDispatchToProps)(GisPointActions),
            ),
        ),
    ),
);
